import { computed, effect, inject, Injectable, resource, signal } from '@angular/core';

import { SiteListSite, SITES_START_OFFSET } from '../models/site-list';
import { SitesGateway } from '../services/sites-gateway.service';
import {
    canLoadMoreSites,
    isCurrentSiteSearch,
    matchesSiteSearch,
    mergeSiteItems,
    shouldReplaceSiteItems,
    toSitesPage,
    toSitesRequest,
} from '../utils/site-list-resource.utils';

import { SiteListResourceRequest, SiteListState, LoadedSitesPage } from './site-list-resource.types';

export const SITES_PAGE_SIZE = 10;

const EMPTY_STATE: SiteListState = {
    items: [],
    keyword: '',
    searchToken: '',
    totalCount: 0,
};

@Injectable({ providedIn: 'root' })
export class SiteListResource {
    private readonly gateway = inject(SitesGateway);
    private readonly request = signal<SiteListResourceRequest>({
        offset: SITES_START_OFFSET,
        limit: SITES_PAGE_SIZE,
        keyword: '',
        refreshToken: crypto.randomUUID(),
        searchToken: crypto.randomUUID(),
    });
    private readonly state = signal<SiteListState>(EMPTY_STATE);
    private readonly page = resource({
        params: this.request,
        loader: async ({ params }): Promise<LoadedSitesPage> => {
            const request = toSitesRequest(params);
            const response = await this.gateway.getSites(request);

            return {
                request,
                response: toSitesPage(response),
            };
        },
    });

    readonly items = computed<readonly SiteListSite[]>(() => {
        const request = this.request();
        const state = this.state();

        return matchesSiteSearch(state, request) ? state.items : [];
    });
    readonly totalCount = computed(() => {
        const request = this.request();
        const state = this.state();

        return matchesSiteSearch(state, request) ? state.totalCount : 0;
    });
    readonly keyword = computed(() => this.request().keyword);
    readonly loading = computed(() => this.page.isLoading() && this.items().length === 0);
    readonly refreshing = computed(() => this.page.isLoading() && this.items().length > 0);
    readonly error = this.page.error;
    readonly lastLoadedPage = computed(() => (this.page.hasValue() ? this.page.value() : undefined));

    constructor() {
        effect(() => {
            const loadedPage = this.lastLoadedPage();
            if (!loadedPage) return;

            const { request, response } = loadedPage;
            this.state.update((state) => ({
                items: shouldReplaceSiteItems(state, request)
                    ? response.results
                    : mergeSiteItems(state.items, response.results),
                keyword: request.keyword,
                searchToken: request.searchToken,
                totalCount: response.totalCount,
            }));
        });
    }

    search(keyword: string): void {
        const normalizedKeyword = keyword.trim();
        const currentRequest = this.request();
        if (isCurrentSiteSearch(currentRequest, normalizedKeyword)) return;

        this.request.set({
            ...currentRequest,
            offset: SITES_START_OFFSET,
            keyword: normalizedKeyword,
            searchToken: crypto.randomUUID(),
        });
    }

    loadMore(): void {
        const loadedCount = this.items().length;
        const currentRequest = this.request();
        const canLoadMore = canLoadMoreSites({
            error: this.error(),
            isLoading: this.page.isLoading(),
            loadedCount,
            request: currentRequest,
            totalCount: this.totalCount(),
        });
        if (!canLoadMore) return;

        this.request.set({ ...currentRequest, offset: loadedCount });
    }

    reload(): void {
        this.request.update((request) => ({
            ...request,
            offset: SITES_START_OFFSET,
            refreshToken: crypto.randomUUID(),
        }));
    }
}
